import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, CheckCircle, Building2, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { isEmailInCompanyDomain, resolveCompanyDomain } from '../utils/companyDomain';
import { teamApi } from '../services/api';
import { getErrorMessage } from '../lib/utils';
import {
  getCityOptionsForCountry,
  getPhoneCodeForCountry,
  getStateOptionsForCountry,
  useCanonicalCountryOptions,
} from '../utils/countries';

const BRANCH_TYPES = ['Regional Office', 'Sales Office', 'Warehouse', 'Distribution Center', 'Representative Office'];

const createEmptyBranch = () => ({
  id: '',
  branch_name: '',
  branch_type: '',
  street_address: '',
  country: '',
  state_region: '',
  city: '',
  contact_name: '',
  contact_designation: '',
  contact_email: '',
  contact_country_code: '+1',
  contact_mobile: '',
  status: 'active',
});

const DEFAULT_BRANCH_IDS = new Set(['branch-001', 'branch-002']);
const DEFAULT_BRANCH_EMAILS = new Set(['shanghai.office@globalfreight.com', 'la.branch@globalfreight.com']);

const removeDefaultSeedBranches = (items) => {
  if (!Array.isArray(items)) return [];
  return items.filter((branch) => {
    const id = (branch?.id || '').trim().toLowerCase();
    const email = (branch?.contact_email || '').trim().toLowerCase();
    const name = (branch?.branch_name || '').trim().toLowerCase();
    const isKnownDefaultName = name === 'shanghai office' || name === 'los angeles branch';
    return !DEFAULT_BRANCH_IDS.has(id) && !DEFAULT_BRANCH_EMAILS.has(email) && !isKnownDefaultName;
  });
};

const firstNonEmpty = (...values) => values.find((v) => typeof v === 'string' && v.trim() !== '') || '';

const normalizeBranchName = (name) => String(name || '').trim().toLowerCase();

const readStoredBranches = (key) => {
  if (typeof window === 'undefined' || !key) return [];
  try {
    const stored = window.localStorage.getItem(key);
    if (!stored) return [];
    return removeDefaultSeedBranches(JSON.parse(stored));
  } catch {
    return [];
  }
};

const mergeBranches = (...branchLists) => {
  const merged = new Map();

  branchLists.flat().forEach((branch) => {
    if (!branch || !(branch.branch_name || branch.city || branch.contact_email)) return;
    const branchName = firstNonEmpty(branch.branch_name, branch.city, 'Branch');
    const key = normalizeBranchName(branchName);
    if (!key) return;

    const existing = merged.get(key) || {};
    merged.set(key, {
      ...branch,
      ...existing,
      id: existing.id || branch.id || generateBranchId(),
      branch_name: firstNonEmpty(existing.branch_name, branchName),
      branch_type: firstNonEmpty(existing.branch_type, branch.branch_type, 'Branch Office'),
      street_address: firstNonEmpty(existing.street_address, branch.street_address),
      country: firstNonEmpty(existing.country, branch.country),
      state_region: firstNonEmpty(existing.state_region, branch.state_region),
      city: firstNonEmpty(existing.city, branch.city),
      contact_name: firstNonEmpty(existing.contact_name, branch.contact_name),
      contact_designation: firstNonEmpty(existing.contact_designation, branch.contact_designation),
      contact_email: firstNonEmpty(existing.contact_email, branch.contact_email),
      contact_country_code: firstNonEmpty(existing.contact_country_code, branch.contact_country_code, '+1'),
      contact_mobile: firstNonEmpty(existing.contact_mobile, branch.contact_mobile),
      status: firstNonEmpty(existing.status, branch.status, 'active'),
    });
  });

  return removeDefaultSeedBranches(Array.from(merged.values()));
};

const generateBranchId = () => `branch-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Colleagues = () => {
  const countryOptions = useCanonicalCountryOptions();
  const countries = countryOptions.map((option) => option.value);
  const phoneCodeOptions = useMemo(() => (
    Array.from(
      new Set(countryOptions.map((option) => getPhoneCodeForCountry(option.value)))
    )
      .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))
      .map((code) => ({ code }))
  ), [countryOptions]);
  const { member, user } = useAuth();
  const storageKey =
    member?.company_id || user?.company_id
      ? `company_branches_${member?.company_id || user?.company_id}`
      : null;

  const [branches, setBranches] = useState([]);
  const [showBranchDialog, setShowBranchDialog] = useState(false);
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [branchForm, setBranchForm] = useState(createEmptyBranch());
  const [savingBranch, setSavingBranch] = useState(false);
  const [registrationCountry, setRegistrationCountry] = useState('');
  const companyDomain = resolveCompanyDomain({
    companyWebsite: member?.company_website,
    companyEmail: member?.email,
    userEmail: user?.email
  });

  // Load persisted branches once the authenticated member/company context is available.
  useEffect(() => {
    if (!storageKey) return;
    setBranches(readStoredBranches(storageKey));
  }, [storageKey]);

  // Persist branches locally so they don't disappear on refresh.
  useEffect(() => {
    if (typeof window === 'undefined' || !storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(branches));
    } catch {
      // Ignore storage errors
    }
  }, [branches, storageKey]);

  // Backfill branches from registration data and active team-member branch assignments.
  useEffect(() => {
    const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
    const companyId = member?.company_id || user?.company_id;
    if (!token || !companyId) return;

    let cancelled = false;

    const syncBranchesFromServer = async () => {
      let registrationBranches = [];
      let teamBranches = [];
      let nextRegistrationCountry = '';

      try {
        const response = await axios.get(`${API_URL}/api/company-registration/my-registration`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const reg = response.data || {};
        nextRegistrationCountry = firstNonEmpty(reg.company_info?.country, reg.country);
        const regBranches = Array.isArray(reg.branches) ? reg.branches : [];

        if (regBranches.length > 0) {
          registrationBranches = regBranches
            .filter((b) => b && (b.branch_name || b.city || b.country))
            .map((b) => ({
              id: b.id || generateBranchId(),
              branch_name: firstNonEmpty(b.branch_name, b.city, 'Branch'),
              branch_type: firstNonEmpty(b.branch_type, 'Branch Office'),
              street_address: firstNonEmpty(b.street_address),
              country: firstNonEmpty(b.country, member?.country),
              state_region: firstNonEmpty(b.state_region),
              city: firstNonEmpty(b.city),
              contact_name: firstNonEmpty(b.contact_name, member?.individual_name, user?.full_name),
              contact_designation: firstNonEmpty(b.contact_designation),
              contact_email: firstNonEmpty(b.contact_email, member?.email, user?.email),
              contact_country_code: firstNonEmpty(b.contact_country_code, '+1'),
              contact_mobile: firstNonEmpty(b.contact_mobile, member?.phone),
              status: 'active'
            }));
        }
      } catch {
        // Team-member branch assignments are still enough to rebuild the branch list.
      }

      try {
        const response = await teamApi.getTeamMembers(companyId);
        const teamMembers = Array.isArray(response.data) ? response.data : [];
        teamBranches = teamMembers
          .filter((teamMember) => teamMember?.is_active !== false && teamMember?.branch)
          .map((teamMember) => {
            const cityParts = String(teamMember.city || '').split(',').map((part) => part.trim()).filter(Boolean);
            return {
              id: `team-branch-${normalizeBranchName(teamMember.branch).replace(/[^a-z0-9]+/g, '-')}`,
              branch_name: teamMember.branch,
              branch_type: 'Branch Office',
              country: cityParts[1] || member?.country || '',
              city: cityParts[0] || teamMember.city || '',
              contact_name: firstNonEmpty(teamMember.full_name, teamMember.email),
              contact_designation: firstNonEmpty(teamMember.designation, teamMember.role),
              contact_email: firstNonEmpty(teamMember.email),
              contact_country_code: '+1',
              contact_mobile: firstNonEmpty(teamMember.phone),
              status: 'active',
            };
          });
      } catch (error) {
        console.debug('Team-member branch assignments unavailable:', error);
      }

      if (!cancelled && nextRegistrationCountry) {
        setRegistrationCountry(nextRegistrationCountry);
      }

      const memberBranchName = firstNonEmpty(member?.branch_name);
      const profileBranch = memberBranchName
        ? [{
          id: generateBranchId(),
          branch_name: memberBranchName,
          branch_type: 'Branch Office',
          street_address: firstNonEmpty(member?.street_address),
          country: firstNonEmpty(member?.country),
          state_region: firstNonEmpty(member?.state_region),
          city: firstNonEmpty(member?.city),
          contact_name: firstNonEmpty(member?.individual_name, user?.full_name),
          contact_designation: '',
          contact_email: firstNonEmpty(member?.email, user?.email),
          contact_country_code: '+1',
          contact_mobile: firstNonEmpty(member?.phone),
          status: 'active'
        }]
        : [];

      if (!cancelled) {
        setBranches((currentBranches) =>
          mergeBranches(currentBranches, readStoredBranches(storageKey), registrationBranches, teamBranches, profileBranch)
        );
      }
    };

    syncBranchesFromServer();
    return () => { cancelled = true; };
  }, [member, storageKey, user]);

  const activeBranchCount = branches.filter((branch) => branch.status === 'active').length;
  const stateOptions = useMemo(
    () => getStateOptionsForCountry(branchForm.country),
    [branchForm.country]
  );
  const cityOptions = useMemo(
    () => getCityOptionsForCountry(branchForm.country, branchForm.state_region || null),
    [branchForm.country, branchForm.state_region]
  );

  const resetBranchForm = () => {
    setBranchForm(createEmptyBranch());
    setEditingBranchId(null);
  };

  const handleDialogState = (open) => {
    setShowBranchDialog(open);
    if (!open) {
      resetBranchForm();
    }
  };

  const handleOpenAddBranch = () => {
    resetBranchForm();
    const selectedCountry = registrationCountry || member?.country || '';
    setBranchForm((prev) => ({
      ...prev,
      country: selectedCountry,
      contact_country_code: getPhoneCodeForCountry(selectedCountry),
    }));
    setShowBranchDialog(true);
  };

  const handleOpenBranchDetails = (branch) => {
    setBranchForm({
      ...branch,
      contact_country_code: branch.contact_country_code || getPhoneCodeForCountry(branch.country),
    });
    setEditingBranchId(branch.id);
    setShowBranchDialog(true);
  };

  const handleFieldChange = (field, value) => {
    setBranchForm((previous) => {
      if (field === 'country') {
        return {
          ...previous,
          country: value,
          state_region: '',
          city: '',
          contact_country_code: getPhoneCodeForCountry(value),
        };
      }

      if (field === 'state_region') {
        return {
          ...previous,
          state_region: value,
          city: '',
        };
      }

      return { ...previous, [field]: value };
    });
  };

  const validateBranchForm = () => {
    const requiredFields = [
      'branch_name',
      'branch_type',
      'street_address',
      'country',
      'city',
      'contact_name',
      'contact_email',
      'contact_mobile',
    ];

    const hasMissingRequiredField = requiredFields.some((field) => {
      const value = branchForm[field];
      return typeof value === 'string' ? !value.trim() : !value;
    });

    if (hasMissingRequiredField) {
      toast.error('Please fill all required branch fields.');
      return false;
    }

    if (!isEmailInCompanyDomain(branchForm.contact_email, companyDomain)) {
      toast.error(`Branch contact email must use @${companyDomain}`);
      return false;
    }

    return true;
  };

  const handleSaveBranch = async (e) => {
    e.preventDefault();
    if (!validateBranchForm()) return;

    if (editingBranchId) {
      setBranches((previous) =>
        previous.map((branch) =>
          branch.id === editingBranchId
            ? { ...branchForm, id: editingBranchId, status: branchForm.status || 'active' }
            : branch
        )
      );
      toast.success('Branch updated successfully.');
    } else {
      setSavingBranch(true);
      try {
        const phone = `${branchForm.contact_country_code || ''}${branchForm.contact_mobile || ''}`.trim();
        await teamApi.inviteTeamMember(
          {
            email: branchForm.contact_email.trim().toLowerCase(),
            full_name: branchForm.contact_name.trim(),
            role: 'Company Member',
            phone,
            designation: branchForm.contact_designation?.trim() || '',
            branch: branchForm.branch_name.trim(),
            city: branchForm.city.trim(),
          },
          member?.member_id || user?.member_id
        );
      } catch (error) {
        toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to invite branch contact.'));
        return;
      } finally {
        setSavingBranch(false);
      }

      setBranches((previous) => [
        ...previous,
        { ...branchForm, id: generateBranchId(), status: 'active' },
      ]);
      toast.success('Branch added and contact invitation sent successfully.');
    }

    setShowBranchDialog(false);
    resetBranchForm();
  };

  const handleDeleteBranch = (branchId, e) => {
    if (e) e.stopPropagation();

    const branchToDelete = branches.find((branch) => branch.id === branchId);
    setBranches((previous) => previous.filter((branch) => branch.id !== branchId));

    if (editingBranchId === branchId) {
      setShowBranchDialog(false);
      resetBranchForm();
    }

    toast.success(`${branchToDelete?.branch_name || 'Branch'} deleted.`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-gray-900">Company Branches</h1>
          <p className="text-gray-600 mt-1">Manage your company branches</p>
        </div>
        <Button className="w-full bg-blue-600 text-white hover:bg-blue-700 md:w-auto" onClick={handleOpenAddBranch}>
          <Plus size={18} className="mr-2" />
          Add Branch
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">Active Branches</p>
                <p className="text-3xl font-bold text-blue-700 mt-1">{activeBranchCount}</p>
                <p className="text-xs text-blue-700 mt-1">{branches.length} total branches</p>
              </div>
              <CheckCircle size={40} className="text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MapPin size={18} className="mr-2 text-blue-600" />
            Company Branches
          </CardTitle>
        </CardHeader>
        <CardContent>
          {branches.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-lg">
              <Building2 size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No branches added yet.</p>
              <Button className="mt-4" variant="outline" onClick={handleOpenAddBranch}>
                <Plus size={16} className="mr-2" />
                Add Your First Branch
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {branches.map((branch) => (
                <div
                  key={branch.id}
                  className="flex flex-col gap-4 rounded-lg bg-gray-50 p-4 transition-colors hover:bg-gray-100 cursor-pointer sm:flex-row sm:items-center sm:justify-between"
                  onClick={() => handleOpenBranchDetails(branch)}
                >
                  <div className="flex min-w-0 items-start space-x-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                      <Building2 size={20} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="break-words font-semibold text-gray-900">{branch.branch_name}</h3>
                      <p className="break-words text-sm text-gray-600">{branch.branch_type || 'No branch type'}</p>
                      <p className="break-words text-sm text-gray-500">
                        {[branch.city, branch.country].filter(Boolean).join(', ') || 'Location not set'}
                      </p>
                      <p className="break-all text-sm text-gray-500">{branch.contact_email || 'No contact email'}</p>
                    </div>
                  </div>

                  <div className="flex w-full shrink-0 items-center justify-end space-x-3 sm:w-auto">
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle size={12} className="mr-1" />
                      Active
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => handleDeleteBranch(branch.id, e)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showBranchDialog} onOpenChange={handleDialogState}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBranchId ? 'Branch Details' : 'Add Branch'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBranch} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>
                  Branch Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={branchForm.branch_name}
                  onChange={(e) => handleFieldChange('branch_name', e.target.value)}
                  placeholder="e.g., Shanghai Office"
                  required
                />
              </div>
              <div>
                <Label>
                  Branch Type <span className="text-red-500">*</span>
                </Label>
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  value={branchForm.branch_type}
                  onChange={(e) => handleFieldChange('branch_type', e.target.value)}
                  required
                >
                  <option value="">Select type</option>
                  {BRANCH_TYPES.map((branchType) => (
                    <option key={branchType} value={branchType}>
                      {branchType}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>
                  Street Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={branchForm.street_address}
                  onChange={(e) => handleFieldChange('street_address', e.target.value)}
                  placeholder="Branch address"
                  required
                />
              </div>
              <div>
                <Label>
                  Country <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={branchForm.country}
                  disabled
                  className="bg-gray-100 text-gray-700 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Fixed to registration country</p>
              </div>
              <div>
                <Label>State / Region</Label>
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  value={branchForm.state_region}
                  onChange={(e) => handleFieldChange('state_region', e.target.value)}
                  disabled={!branchForm.country || stateOptions.length === 0}
                >
                  <option value="">
                    {!branchForm.country
                      ? 'Select country first'
                      : stateOptions.length === 0
                        ? 'No states available'
                        : 'Select state / region'}
                  </option>
                  {stateOptions.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>
                  City <span className="text-red-500">*</span>
                </Label>
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  value={branchForm.city}
                  onChange={(e) => handleFieldChange('city', e.target.value)}
                  required
                  disabled={!branchForm.country || cityOptions.length === 0}
                >
                  <option value="">
                    {!branchForm.country
                      ? 'Select country first'
                      : cityOptions.length === 0
                        ? 'No cities available'
                        : 'Select city'}
                  </option>
                  {cityOptions.map((city) => (
                    <option key={`${city.value}-${city.label}`} value={city.value}>
                      {city.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 pt-2 border-t">
                <p className="text-sm font-medium text-gray-700 mb-2">Branch Contact</p>
              </div>
              <div>
                <Label>
                  Contact Person <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={branchForm.contact_name}
                  onChange={(e) => handleFieldChange('contact_name', e.target.value)}
                  placeholder="Full name"
                  required
                />
              </div>
              <div>
                <Label>Designation</Label>
                <Input
                  value={branchForm.contact_designation}
                  onChange={(e) => handleFieldChange('contact_designation', e.target.value)}
                  placeholder="Role"
                />
              </div>
              <div>
                <Label>
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  value={branchForm.contact_email}
                  onChange={(e) => handleFieldChange('contact_email', e.target.value)}
                  placeholder={companyDomain ? `email@${companyDomain}` : 'email@company.com'}
                  required
                />
                {companyDomain && (
                  <p className="text-xs text-gray-500 mt-1">
                    Allowed domain: @{companyDomain}
                  </p>
                )}
              </div>
              <div>
                <Label>
                  Mobile <span className="text-red-500">*</span>
                </Label>
                <div className="flex space-x-2">
                  <select
                    className="w-24 px-2 py-2 border rounded-md text-sm"
                    value={branchForm.contact_country_code}
                    onChange={(e) => handleFieldChange('contact_country_code', e.target.value)}
                  >
                    {phoneCodeOptions.map((countryCode) => (
                      <option key={countryCode.code} value={countryCode.code}>
                        {countryCode.code}
                      </option>
                    ))}
                  </select>
                  <Input
                    className="flex-1"
                    value={branchForm.contact_mobile}
                    onChange={(e) => handleFieldChange('contact_mobile', e.target.value)}
                    placeholder="Mobile"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              {editingBranchId ? (
                <Button
                  type="button"
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => handleDeleteBranch(editingBranchId)}
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete Branch
                </Button>
              ) : (
                <div />
              )}
              <div className="flex space-x-3">
                <Button type="button" variant="outline" onClick={() => setShowBranchDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={savingBranch}>
                  {editingBranchId ? 'Update Branch' : savingBranch ? 'Saving...' : 'Save Branch'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Colleagues;
